import { useState, useMemo } from 'react'

import {
  Button,
  Link,
  PlusIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { TagEditDialog } from '~publish/components/TagEditDialog/TagEditDialog'
import { useAccount } from '~publish/contexts/AccountContext'
import type { Tag } from '~publish/gql/graphql'
import { useTags } from '~publish/hooks/useTags'
import UserEntity from '~publish/legacy/user/UserEntity'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

import { NewSettingsPageLayout } from '../components/NewSettingsPageLayout'
import { TagsLoadingSkeleton } from './components/TagsLoadingSkeleton'
import { TagsErrorState } from './components/TagsErrorState'
import { TagsEmptyState } from './components/TagsEmptyState'
import { TagsList } from './components/TagsList'


const TagsSubtitle = (): JSX.Element => (
  <>
    Tags are visible to everyone in your organization.{' '}
    <Link
      href={`https://support.buffer.com/article/585-creating-and-managing-campaigns?${HC_UTM_PARAMS}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </Link>
  </>
)

export const Tags = (): JSX.Element => {
  const isMobile = useIsMobileScreenSize()
  const account = useAccount()
  const isAdmin = UserEntity.isAdmin(account)

  const [searchFilter, setSearchFilter] = useState('')

  const {
    tags,
    loading: loadingTags,
    error: errorLoadingTags,
    createTag,
    hasCreatePermission,
  } = useTags()

  const filteredTags = useMemo(() => {
    if (!searchFilter.trim()) return tags
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchFilter.toLowerCase()),
    )
  }, [tags, searchFilter])

  const handleCreateTagSubmit = async (tag: Tag): Promise<void> => {
    try {
      await createTag({ name: tag.name, color: tag.color })
    } catch {
      toast.error('Failed to create tag. Please try again later.')
    }
  }

  // Action button for creating new tags
  const newTagButton = isAdmin && hasCreatePermission && (
    <TagEditDialog onSubmit={handleCreateTagSubmit}>
      <Button>
        {!isMobile && <PlusIcon />}
        {'New Tag'}
      </Button>
    </TagEditDialog>
  )

  // View state logic
  const showEmptyState = !loadingTags && !errorLoadingTags && tags.length === 0
  const showTagsList = !loadingTags && !errorLoadingTags && tags.length > 0

  return (
    <NewSettingsPageLayout
      title="Tags"
      subtitle={<TagsSubtitle />}
      actions={showTagsList ? newTagButton : null}
    >
      {loadingTags && <TagsLoadingSkeleton />}
      {errorLoadingTags && <TagsErrorState />}
      {showEmptyState && (
        <TagsEmptyState
          isAdmin={isAdmin}
          hasCreatePermission={hasCreatePermission}
          onCreateTag={handleCreateTagSubmit}
        />
      )}
      {showTagsList && (
        <TagsList
          searchFilter={searchFilter}
          onSearchChange={setSearchFilter}
          filteredTags={filteredTags}
          isAdmin={isAdmin}
        />
      )}
    </NewSettingsPageLayout>
  )
}

export default Tags
